const FONT_ALIASES = {
  angsana: 'Angsana New',
  THSarabunNew: 'TH Sarabun New',
  sarabun: 'Sarabun',
  Prompt: 'Prompt',
}

export const EXCEL_FONT_OPTIONS = [
  'TH SarabunPSK', 'TH Sarabun New', 'Sarabun', 'Prompt',
  'Angsana New', 'Arial', 'Calibri', 'Tahoma', 'Times New Roman',
].map((name) => ({ label: name, value: name }))

export const isValidExcelFontSize = (value) =>
  Number.isFinite(Number(value)) && Number(value) >= 1 && Number(value) <= 409

export const getExcelProfileFont = (profile = {}, layout = {}) => {
  const configured = typeof profile.fontFamily === 'string'
    ? profile.fontFamily.trim() : ''
  const family = configured || layout?.fontFamily || 'TH Sarabun New'
  const fontFamily = FONT_ALIASES[family] || family
  return {
    fontFamily,
    fontSize: isValidExcelFontSize(profile.fontSize) ? Number(profile.fontSize) : 12,
    previewFontFamily: {
      'TH SarabunPSK': 'CKD_THSarabun',
      'TH Sarabun New': 'CKD_THSarabunNew',
    }[fontFamily] || fontFamily,
  }
}

const hasValue = (value) =>
  value !== null && typeof value !== 'undefined' && value !== ''

const normalizeType = (value = '') => {
  const type = `${value || ''}`.trim().toLowerCase()

  if (['int', 'integer', 'float', 'double', 'decimal', 'numeric'].includes(type)) {
    return 'number'
  }
  if (['date', 'datetime', 'timestamp', 'time'].includes(type)) return type
  if (['bool', 'boolean'].includes(type)) return 'boolean'

  return type || ''
}

const inferColumnType = (rows = [], field = '') => {
  const sample = rows
    .map((row) => row?.[field])
    .find((value) => hasValue(value))

  if (typeof sample === 'number') return 'number'
  if (typeof sample === 'boolean') return 'boolean'
  if (
    typeof sample === 'string' &&
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2})?)?/.test(sample)
  ) {
    return sample.includes('T') ? 'datetime' : 'date'
  }

  return 'text'
}

export const getExcelDataSources = (report = {}) =>
  (report?.dataSourceData?.list || [])
    .map((dataSource, index) => {
      const rows = Array.isArray(dataSource?.fieldVariable?.value)
        ? dataSource.fieldVariable.value
        : []
      const configuredColumns = Array.isArray(
        dataSource?.fieldVariable?.columns,
      )
        ? dataSource.fieldVariable.columns
        : []
      const rowColumns = rows.reduce((fields, row) => {
        Object.keys(row || {}).forEach((field) => {
          if (!fields.includes(field)) fields.push(field)
        })
        return fields
      }, [])
      const columns = configuredColumns.length ? configuredColumns : rowColumns
      const id = `${
        dataSource.dataSourceId ||
        dataSource.dataSourceVariableName ||
        dataSource.name ||
        index
      }`
      const label =
        dataSource.name ||
        dataSource.dataSourceVariableName ||
        `Datasource ${index + 1}`

      return {
        columns,
        id,
        label,
        raw: dataSource,
        rows,
      }
    })
    .filter(({ columns }) => columns.length > 0)

export const getExcelProfile = (report = {}, dataSourceId = '') => {
  const profile =
    report?.publishData?.excelProfile || report?.excelProfile || {}

  return profile?.dataSources?.[dataSourceId] || profile || {}
}

export const createAutoExcelColumns = (dataSource = {}, profile = {}) => {
  const rows = dataSource.rows || []
  const props = dataSource?.raw?.fieldVariable?.props || {}
  const configuredColumns = profile?.columns || {}
  const columnOverrides = Array.isArray(configuredColumns)
    ? configuredColumns.reduce((result, column) => {
        if (column?.field) result[column.field] = column
        return result
      }, {})
    : configuredColumns
  const order = Array.isArray(profile?.order) ? profile.order : []

  return (dataSource.columns || [])
    .map((field, index) => {
      const metadata = props[field] || {}
      const override = columnOverrides?.[field] || {}
      const exportable = metadata.exportable !== false
      const metadataType = normalizeType(
        metadata.excelType || metadata.dataType || metadata.type,
      )

      return {
        field,
        label:
          override.label ||
          metadata.label ||
          metadata.title ||
          metadata.displayName ||
          field,
        type:
          normalizeType(override.type) ||
          metadataType ||
          inferColumnType(rows, field),
        visible: exportable && override.visible !== false,
        exportable,
        width: Number(override.width || metadata.width || 18),
        sourceIndex: index,
      }
    })
    .sort((left, right) => {
      const leftOrder = order.indexOf(left.field)
      const rightOrder = order.indexOf(right.field)

      if (leftOrder < 0 && rightOrder < 0) {
        return left.sourceIndex - right.sourceIndex
      }
      if (leftOrder < 0) return 1
      if (rightOrder < 0) return -1
      return leftOrder - rightOrder
    })
}

export const createExcelProfileJson = (
  columns = [],
  options = {},
  autoColumns = [],
  autoOptions = {},
) => {
  const autoColumnMap = autoColumns.reduce((result, column) => {
    result[column.field] = column
    return result
  }, {})
  const profileFields = ['label', 'type', 'visible', 'width']
  const profileColumns = columns.reduce((result, column) => {
    const autoColumn = autoColumnMap[column.field]
    const override = {}

    profileFields.forEach((field) => {
      if (!autoColumn || column[field] !== autoColumn[field]) {
        override[field] = column[field]
      }
    })
    if (Object.keys(override).length) result[column.field] = override
    return result
  }, {})
  const order = columns.map(({ field }) => field)
  const autoOrder = autoColumns.map(({ field }) => field)
  const hasCustomOrder =
    !autoColumns.length || order.join('|') !== autoOrder.join('|')

  return {
    ...(options.fileName && options.fileName !== autoOptions.fileName
      ? { fileName: options.fileName }
      : {}),
    ...(options.sheetName && options.sheetName !== autoOptions.sheetName
      ? { sheetName: options.sheetName }
      : {}),
    ...(typeof options.fontFamily === 'string' && options.fontFamily.trim()
      ? { fontFamily: options.fontFamily.trim() }
      : {}),
    ...(isValidExcelFontSize(options.fontSize)
      ? { fontSize: Number(options.fontSize) }
      : {}),
    ...(hasCustomOrder ? { order } : {}),
    ...(Object.keys(profileColumns).length ? { columns: profileColumns } : {}),
  }
}
